import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { WithFooterWrapper } from '@terminal/components/with-footer-wrapper/WithFooterWrapper';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { CompanyToThemeWidget } from '@terminal/widgets/company-to-theme-widget/CompanyToThemeWidget';
import { Button } from '@toggle/design-system/src/components/button/Button';
import { Drawer } from '@toggle/design-system/src/components/drawer/Drawer';
import { clsx } from 'clsx';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import styles from '../theme-overview/ThemeOverview.module.scss';
import { usePerformanceStore } from '../theme-overview/tab-content/performance/hooks/usePerformanceStore';
import {
  THEME_OVERVIEW_CONTENT_ID,
  ThemeOverviewHeader,
} from '../theme-overview/theme-overview-header/ThemeOverviewHeader';
import msThemeBaskets from './ms-baskets.json';
import { MSTabContent } from './tab-content/MSTabContent';

/** Morgan Stanley POC */
export const MSThemeOverview = () => {
  const { t } = useTranslation([
    'widget',
    'screener',
    'common',
    'calendar',
    'earnings',
    'tooltip',
    'chart',
    'insights',
    'scenario',
    'watchlistManager',
  ]);
  const { params, deleteQueryParam } = useQueryParams();
  const { themeId = '' } = useParams();
  const { initStore: initPerfStore, resetStore } = usePerformanceStore(
    state => ({
      initStore: state.initStore,
      resetStore: state.resetStore,
    })
  );
  const { navigateToPage } = useNavigateToAssetOverview();

  const themeData = msThemeBaskets.find(
    basket => basket.basket_ticker === themeId
  );
  const entityTags = useMemo(
    () =>
      themeData?.constituents.map(constituent => constituent.entity_tag) ?? [],
    [themeData]
  );

  useEffect(() => {
    if (entityTags.length) {
      initPerfStore({ t, themeId, entityTags });
    }

    return resetStore;
  }, [entityTags]);

  const closeDrawer = () => {
    deleteQueryParam([
      queryKeys.activeDrawer,
      queryKeys.activeDrawerTab,
      queryKeys.entityTag,
    ]);
  };

  const isDrawerOpen =
    params[queryKeys.activeDrawer] === queryValues.companyToThemeDrawer;

  const data = {
    name: themeData?.basket_name,
    description: themeData?.llm_description,
    id: themeData?.basket_ticker,
    attached_themes: themeData?.attached_themes,
    constituents: themeData?.constituents,
  };

  if (!themeData) {
    return (
      <div className={styles.errorMessageWrapper}>
        <ErrorMessageWrapper
          displayIcon
          variant="empty"
          data-testid="theme-overview-empty-error-message"
        />
      </div>
    );
  }

  return (
    <div className={styles.root} data-testid="theme-overview">
      <ErrorBoundaryWrapper showBorder={false}>
        <ThemeOverviewHeader
          // @ts-expect-error
          theme={data}
        />
        <WithFooterWrapper
          shouldHide={
            params[queryKeys.activeDrawer] === queryValues.companyToThemeDrawer
          }
          trackingMetadata={{ source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW }}
        >
          <div className={styles.scrollWrapper} id={THEME_OVERVIEW_CONTENT_ID}>
            <div
              className={clsx(styles.content, {
                [styles.industryTheme]: true,
              })}
            >
              <MSTabContent theme={data} />
            </div>
          </div>
        </WithFooterWrapper>
      </ErrorBoundaryWrapper>

      <Drawer
        handleCloseClick={closeDrawer}
        isOpen={isDrawerOpen}
        headerButton={
          <Button
            variant="empty"
            label={t('widget:themeOverview.drawer.assetOverview')}
            size="xsmall"
            iconName="Expand"
            onClick={() =>
              navigateToPage({
                entityTag: params[queryKeys.entityTag] ?? '',
                source: POSTHOG_TRACKING_SOURCES.COMPANY_TO_THEME_DRAWER,
              })
            }
          />
        }
      >
        <ErrorBoundaryWrapper resetKeys={[isDrawerOpen]}>
          <DrawerContentWrapper>
            {/* @ts-expect-error */}
            <CompanyToThemeWidget theme={data} />
          </DrawerContentWrapper>
        </ErrorBoundaryWrapper>
      </Drawer>
    </div>
  );
};
